// Twin of the API repo's `packages/features/src/SpeedyFlow/core/deferredCheckout.ts`. Change both together.

import { isChoiceStale } from './choice';

/**
 * The steps a deferred start is made of. Every one of them is a side effect the caller owns; what
 * lives here is only the order they happen in and what decides between them.
 */
export interface DeferredCheckoutSteps {
  /**
   * Ask the product list again and wait for the answer. `answered` is false when the list could not
   * be reached at all, which is not the same as it disagreeing — see below.
   */
  reconcile: () => Promise<{ offered: string | undefined; answered: boolean }>;
  /** Create the Stripe session for the recorded choice, answering its URL. */
  createSession: () => Promise<string>;
  /** The list no longer offers what they picked. Nothing was created. */
  onUnavailable: () => void;
  /** The session exists. */
  onSession: (url: string) => void;
  /** Nothing could be created, for whatever reason the caller cares to read. */
  onFailed: (err: unknown) => void;
}

/**
 * Turn a choice made earlier into a checkout session, but only if the list still stands behind it.
 *
 * **The re-check is the point of this function.** The choice was already judged by the effect in
 * `SpeedyFlow` — and that judgement cannot be trusted at the moment of the press. Signing in
 * invalidates the product list (`refreshPricesForNewSession`), and react-query keeps serving the
 * previous answer with `isSuccess` still true for as long as the refetch takes. In that window the
 * effect compares the signed-out list against the signed-out pick, finds them in agreement, and
 * lets it stand. A visitor quick enough to press the button in that window posts the price they
 * were shown before they had an account.
 *
 * The API refuses that price rather than substituting it (`checkPriceEligibility`, ADR 0002 in
 * zl-api), so the visitor is never charged a total they did not agree to. What they get instead is
 * a checkout tab opened on a request that could not succeed, and a bounce out of it. Asking once
 * more, and waiting for the answer, costs one round trip on a press that is about to create a
 * Stripe session anyway.
 *
 * **An answer that failed to arrive is not a disagreement.** `isChoiceStale` already draws that
 * line for a request still in flight, and it holds just as well for one that never landed: a list
 * we could not reach says nothing about the price, and refusing to start a checkout over it would
 * turn a blip into a purchase nobody can make. The API is still there to refuse it.
 */
export const runDeferredCheckout = async (
  recorded: string | undefined,
  { reconcile, createSession, onUnavailable, onSession, onFailed }: DeferredCheckoutSteps,
): Promise<void> => {
  try {
    const { offered, answered } = await reconcile();
    if (isChoiceStale({ recorded, offered, answered })) {
      onUnavailable();
      return;
    }
    onSession(await createSession());
  } catch (err) {
    onFailed(err);
  }
};
